import React from "react";
import Table, { ColumnsType } from "antd/lib/table"
import { WebApiCustomListsDtoCustomListItemDto, WebApiMemberDtoMemberDto, WebApiModelsMemberField, WebApiModelsCustomListField, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto, WebApiModelsMembershipProduct } from "../../Repository/eventbookyapi"
import { Button, Popconfirm, Space, TableProps } from "antd";
import EditMember from "../Users/EditMember";
import AddNote from "../Users/AddNote";
import AddPayment from "../Users/AddPayment";
import { DeleteOutlined } from "@ant-design/icons";
import { formatDateString } from "../../Helpers/DateHelpers";
import { TextService } from "../../Services/TextService";
import { MemberService } from "../../Services/MemberService";
import { ComponentPropsWithoutRef, useEffect, useState } from "react";
import { CustomListFieldService } from '../../Services/CustomLifFieldService';
import { CustomListItemService } from '../../Services/CustomListItemService';
import { PaymentConfiguration } from "../../Types/PaymentConfiguration";
import Cookies from "universal-cookie";
import AddPaymentLink from "../Users/AddPaymentLink";
import { SavedSorting } from "../../Types/SavedSorting";
import { SortOrder } from "antd/es/table/interface";
import moment from "moment";
import { MemberFieldTypes } from "../../Types/MemberFieldTypes";
import CustomListEditModal from "../../Pages/CustomList/CustomListEditModal";
import CustomListAddModal from "../../Pages/CustomList/CustomListAddModal";
import { CompanyTypeConfiguration } from "../../Types/CompanyTypes";
import { useNavigate } from "react-router-dom";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";

export interface MemberTableProps {
    savedSorting?: any;
    handlePageSizeChanged?: (size: number) => void;
    hasPingPaymentIntegration?: boolean;
    pageSizePreference?: number;
    user?: any;
    style?: React.CSSProperties;
    isMail?: boolean;
    members?: any[];
    memberFieldSettings?: any[];
    membershipProducts?: WebApiModelsMembershipProduct[];
    groups?: any[];
    loadMembers?: () => void;
    customFieldsMap?: Record<number, WebApiModelsCustomListField[]> | null;
    lookupOptionsMap?: Record<number, Record<string, { value: number; label: string }[]>> | null;
    removeMember?: (member: any) => void;
    enableRowClick?: boolean;
}

export const MemberTable = (props: MemberTableProps) => {
    const navigate = useNavigate();

    // If lookup data is not provided, fallback to internal state and fetching (for backward compatibility)
    const [internalCustomFieldsMap, setInternalCustomFieldsMap] = useState<Record<number, WebApiModelsCustomListField[]>>({});
    const [internalLookupOptionsMap, setInternalLookupOptionsMap] = useState<Record<number, Record<string, { value: number; label: string }[]>>>({});
    const customFieldsMap = props.customFieldsMap ?? internalCustomFieldsMap;
    const lookupOptionsMap = props.lookupOptionsMap ?? internalLookupOptionsMap;

    // --- Modal state for custom list modals ---
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editItem, setEditItem] = useState<WebApiCustomListsDtoCustomListItemDto | null>(null);
    const [modalField, setModalField] = useState<WebApiModelsMemberField | null>(null);
    const [modalMember, setModalMember] = useState<WebApiMemberDtoMemberDto | null>(null);
    const [modalCustomListId, setModalCustomListId] = useState<number | null>(null);
    // Use internal state only if not provided in props
    const [loadingModal, setLoadingModal] = useState(false);
    // Track loading state for fetching the edit item
    const [loadingEditItem, setLoadingEditItem] = useState(false);

    // Helper to fetch and cache customFields and lookupOptions for a customListId
    const ensureCustomListData = async (customListId: number) => {
        setLoadingModal(true);
        if (!props.customFieldsMap) {
            if (!internalCustomFieldsMap[customListId]) {
                const fields = await CustomListFieldService.GetCCustomListFields(customListId) as WebApiModelsCustomListField[];
                setInternalCustomFieldsMap(prev => ({ ...prev, [customListId]: fields }));
            }
        }
        if (!props.lookupOptionsMap) {
            if (!internalLookupOptionsMap[customListId]) {
                const items = await CustomListItemService.GetItems(customListId) as WebApiCustomListsDtoCustomListItemDto[];
                const optionsMap: Record<string, { value: number; label: string }[]> = {};
                (items || []).forEach((item: WebApiCustomListsDtoCustomListItemDto) => {
                    let label = item.id?.toString() || '';
                    if (Array.isArray(item.fieldValues) && item.fieldValues.length > 0) {
                        label = item.fieldValues[0].fieldValue;
                    }
                    if (!optionsMap['default']) optionsMap['default'] = [];
                    optionsMap['default'].push({ value: item.id!, label });
                });
                setInternalLookupOptionsMap(prev => ({ ...prev, [customListId]: optionsMap }));
            }
        }
        setLoadingModal(false);
    };

    const GetText = TextService.GetText;
    const [memberToDelete, setMemberToDelete] = useState(0 as number);
    const [sortedInfo, setSortedInfo] = useState(undefined as SavedSorting | undefined);

    useEffect(() => {
        if (props.savedSorting) {
            setSortedInfo(props.savedSorting);
        };
    }, [props])

    const sortAlphabeticly = (function (a: any, b: any, propName: string) {
        const itemAProp = a[propName] != null && a[propName] != undefined ? a[propName].toString().toLowerCase() : "";
        const itemBProp = b[propName] != null && b[propName] != undefined ? b[propName].toString().toLowerCase() : "";
        return itemAProp.localeCompare(itemBProp, 'sv');
    })

    const sortDate = (a: Date | undefined, b: Date | undefined) => {
        if (!a && !b) return 0;
        if (!a) return -1;
        if (!b) return 1;
        return moment(a).diff(moment(b));
    };

    const sortUniqueId = (function (a: any, b: any) {
        if (Number(a.uniqueId) < Number(b.uniqueId)) { return -1; }
        if (Number(a.uniqueId) > Number(b.uniqueId)) { return 1; }
        return 0;
    })

    const onDeleteClick = (memberId: number) => {
        setMemberToDelete(memberId);
    };

    const handleOk = async () => {
        await MemberService.DeleteMember(memberToDelete);
        props.loadMembers && props.loadMembers();
        setMemberToDelete(0);
    };

    const onPaginateChange = (value: any, newPageSize: any) => {
        new Cookies().set('customerPageSize', newPageSize, { maxAge: 31536000 });
        props.handlePageSizeChanged?.(newPageSize);
    };


    const onTableChange = (pagination: any, filters: any, sorter: any, extra: any) => {
        if (sorter != null && sorter.field != null) {
            const cookies = new Cookies();
            const sorting: SavedSorting = { columnKey: sorter.field, sortingDirection: sorter.order };
            cookies.set('customerSorter', JSON.stringify(sorting), { maxAge: 31536000 });
            setSortedInfo({ columnKey: sorter.field.toLowerCase(), sortingDirection: sorter.order });
        }
    }
    const sortIconClassName = 'ant-table-column-title';
    const HeaderCell = (props: ComponentPropsWithoutRef<'th'>) => {
        return (
            <th
                {...props}
                onClick={(e) => {
                    const currentTarget = e.currentTarget as HTMLElement;
                    const sortIconNode = currentTarget.querySelector(`.${sortIconClassName}`);
                    if (sortIconNode?.contains(e.target as HTMLElement)) {
                        props.onClick?.(e);
                    }
                }}
            />
        );
    };

    const components: TableProps['components'] = {
        header: {
            cell: HeaderCell
        }
    };

    const columns: ColumnsType<WebApiMemberDtoMemberDto> = [{
        title: GetText("MemberList_UniqueIdColumn"),
        width: 100,
        dataIndex: 'uniqueid',
        sortOrder: sortedInfo?.columnKey === 'uniqueid' ? sortedInfo?.sortingDirection as SortOrder : undefined,
        key: 'uniqueid',
        render: (_, member: WebApiMemberDtoMemberDto) => (
            <span>{member.uniqueId}</span>
        ),
        sorter: sortUniqueId,
        sortDirections: ['ascend', 'descend', 'ascend'],
    }]

    // Add company name column if user has CRM company type
    if (props.user?.companyType === CompanyTypeConfiguration.CRM) {
        columns.push({
            title: GetText("MemberList_CompanyColumn"),
            width: 150,
            dataIndex: 'company',
            key: 'company',
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.company}</span>
            ),
        });
    }

    const firstNameCol = {
        title: GetText("MemberList_FirstNameColumn"),
        dataIndex: 'firstname',
        key: 'firstname',
        sortOrder: sortedInfo?.columnKey === 'firstname' ? sortedInfo?.sortingDirection as SortOrder : undefined,
        width: 150,
        render: (_: any, member: WebApiMemberDtoMemberDto) => (
            <span>{member.firstName}<br />
            </span>
        ),
        sorter: { compare: (a: WebApiMemberDtoMemberDto, b: WebApiMemberDtoMemberDto) => sortAlphabeticly(a, b, "firstName"), multiple: 2 },
        sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    }

    const lastNameCol = {
        title: GetText("MemberList_LastNameColumn"),
        dataIndex: 'lastname',
        key: 'lastname',
        sortOrder: sortedInfo?.columnKey === 'lastname' ? sortedInfo?.sortingDirection as SortOrder : undefined,
        width: 150,
        render: (_: any, member: WebApiMemberDtoMemberDto) => (
            <span>{member.lastName}<br />
            </span>
        ),
        sorter: { compare: (a: WebApiMemberDtoMemberDto, b: WebApiMemberDtoMemberDto) => sortAlphabeticly(a, b, "lastName") },
        sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    }

    const firstNameFieldColumnOrder = props.memberFieldSettings?.find((f: WebApiModelsMemberField) => f.columnType === MemberFieldTypes.FirstName)?.columnOrder;
    const lastNameFieldColumnOrder = props.memberFieldSettings?.find((f: WebApiModelsMemberField) => f.columnType === MemberFieldTypes.LastName)?.columnOrder;

    if (firstNameFieldColumnOrder < lastNameFieldColumnOrder) {
        columns.push(firstNameCol);
        columns.push(lastNameCol);
    } else {
        columns.push(lastNameCol);
        columns.push(firstNameCol);
    }

    columns.push(
        {
            title: GetText("MemberList_EmailColumn"),
            dataIndex: 'email',
            key: 'email',
            sortOrder: sortedInfo?.columnKey === 'email' ? sortedInfo?.sortingDirection as SortOrder : undefined,
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.email}<br /></span>
            ),
            sorter: { compare: (a, b) => sortAlphabeticly(a, b, "email") },
            sortDirections: ['ascend', 'descend', 'ascend'],
        },
        {
            title: GetText("MemberList_PhoneColumn"),
            dataIndex: 'phone',
            key: 'phone',
            width: 150,
            sorter: false,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.phoneNumberFormatted}<br /></span>
            ),
        },
        {
            title: GetText("MemberList_GroupsColumn"),
            dataIndex: 'company',
            key: 'company',
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>
                    {member.groupNames?.map((groupName: any, i) => (
                        <>{groupName} <br /></>
                    ))}
                </span>
            ),
        }
    );

    // Add membership column only if there are membership products
    if (props.membershipProducts && props.membershipProducts.length > 0) {
        columns.push({
            title: GetText("MemberList_MembershipColumn"),
            dataIndex: 'membership',
            key: 'membership',
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => {
                const membershipNames: string[] = [];

                // Add primary membership product name
                if (member.membershipProduct) {
                    const membershipProduct = props.membershipProducts?.find(p => p.id === member.membershipProduct);
                    if (membershipProduct?.name) {
                        membershipNames.push(membershipProduct.name);
                    }
                }

                // Add addon membership product names
                if (member.membershipProductAddOn) {
                    member.membershipProductAddOn.forEach(addonId => {
                        const addonProduct = props.membershipProducts?.find(p => p.id === addonId);
                        if (addonProduct?.name) {
                            membershipNames.push(addonProduct.name);
                        }
                    });
                }

                return (
                    <span>
                        {membershipNames.map((membershipName, i) => (
                            <React.Fragment key={i}>
                                {membershipName} <br />
                            </React.Fragment>
                        ))}
                    </span>
                );
            },
        });
    }

    if (props.isMail == true) {
        columns.push({
            title: GetText("MemberList_ActionColumn"),
            key: 'action',
            render: (_, member) => (
                <Space size="middle">
                    <Button size="small" onClick={() => props.removeMember?.(member)}>Ta bort</Button>
                </Space>
            )
        })
    }
    else {
        // Add one column per custom field that is a lookup
        (props.memberFieldSettings ?? [])
            .filter((f: WebApiModelsMemberField) => f.uiControl === 4)
            .forEach((field: WebApiModelsMemberField) => {
                columns.push({
                    title: field.nameAdmin,
                    key: `custom_lookup_${field.id}`,
                    width: 150,
                    render: (_: any, member: WebApiMemberDtoMemberDto) => {
                        const fieldValueObj =
                            member.customFieldValues?.find((fv: any) => String(fv.fieldId) === String(field.id))
                            ?? member.customFieldValues?.find((fv: any) => String(fv.fieldId) === String(field.id));
                        const value = fieldValueObj?.fieldValue;
                        const customListId = field.customListId;
                        if (!customListId) return '';
                        // Try to get the label for the value from lookupOptionsMap
                        let label = value;
                        const options = lookupOptionsMap[customListId]?.default || [];
                        if (value && options.length > 0) {
                            const found = options.find(opt => String(opt.value) === String(value));
                            if (found) label = found.label;
                        }
                        if (value) {
                            return (
                                <a href="#" onClick={async e => {
                                    e.preventDefault();
                                    setLoadingEditItem(true);
                                    setModalField(field);
                                    setModalMember(member);
                                    setModalCustomListId(customListId);
                                    await ensureCustomListData(customListId);
                                    // Fetch all items for this custom list
                                    try {
                                        const items = await CustomListItemService.GetItems(customListId);
                                        // Find the item with the matching id
                                        const foundItem = (items || []).find((item: any) => String(item.id) === String(value));
                                        setEditItem(foundItem || { id: value });
                                    } catch (err) {
                                        setEditItem({ id: value });
                                    }
                                    setEditModalOpen(true);
                                    setLoadingEditItem(false);
                                }}>{label}</a>
                            );
                        } else {
                            return (
                                <a href="#" onClick={async e => {
                                    e.preventDefault();
                                    setModalField(field);
                                    setModalMember(member);
                                    setModalCustomListId(customListId);
                                    await ensureCustomListData(customListId);
                                    setAddModalOpen(true);
                                }}>Lägg till</a>
                            );
                        }
                    }
                });
            });

        columns.push({
            title: GetText("MemberList_LastModifiedColumn"),
            dataIndex: 'lastmodified',
            key: 'lastmodified',
            sortOrder: sortedInfo?.columnKey === 'lastmodified' ? sortedInfo?.sortingDirection as SortOrder : undefined,
            width: 150,
            sorter: { compare: (a, b) => sortDate(a.lastModified, b.lastModified) },
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{formatDateString(member?.lastModified!.toString())}<br />
                </span>
            ),
        },
            {
                title: GetText("MemberList_CreatedColumn"),
                dataIndex: 'createdat',
                key: 'createdat',
                sortOrder: sortedInfo?.columnKey === 'createdat' ? sortedInfo?.sortingDirection as SortOrder : undefined,
                width: 150,
                sorter: { compare: (a, b) => sortDate(a.createdAt, b.createdAt) },
                render: (_, member: WebApiMemberDtoMemberDto) => (
                    <span>{formatDateString(member?.createdAt!.toString())}<br />
                    </span>
                ),
            },
            {
                title: GetText("MemberList_ActionColumn"),
                key: 'action',
                width: 150,
                render: (_, member) => (
                    <Space size="middle" onClick={(e) => e.stopPropagation()}>
                        <EditMember user={props.user} membershipProducts={props.membershipProducts ?? []} memberFieldSettings={props.memberFieldSettings ?? []} loadMembers={props.loadMembers ?? (() => { })} members={props.members ?? []} groups={props.groups} member={member} />
                        <AddNote user={props.user} loadMembers={props.loadMembers} groups={props.groups} member={member}></AddNote>
                        {props.user.paymentConfiguration == PaymentConfiguration.YearlyPayments ? <AddPayment loadMembers={props.loadMembers} membershipProducts={props.membershipProducts} member={member}></AddPayment> : ""}
                        {props.hasPingPaymentIntegration ? <AddPaymentLink user={props.user} loadMembers={props.loadMembers} groups={props.groups} member={member}></AddPaymentLink> : ""}

                        {!props.user.hasOnlyReadRights ? <DeleteOutlined data-testid="delete-member-button" style={{ color: "red", fontSize: "20px" }} onClick={() => onDeleteClick(member?.memberId!)}></DeleteOutlined> : ""}

                        <Popconfirm
                            title={GetText("MemberList_DeleteMemberConfirmMessage")}
                            key={member.email}
                            open={member.memberId === memberToDelete}
                            placement="topRight"
                            onConfirm={handleOk}
                            onCancel={() => setMemberToDelete(0)}
                        >
                        </Popconfirm>
                    </Space>
                ),
            },)
    }

    return (<>
        <Table
            columns={columns}
            style={props.style}
            locale={{ emptyText: GetText("MemberList_EmptyMessage") }}
            className="admin-table"
            rowKey={m => m.memberId!}
            showSorterTooltip={false}
            components={components}
            onChange={onTableChange}
            sortDirections={['ascend', 'descend', 'ascend']}
            pagination={{ pageSize: props.pageSizePreference, locale: { items_per_page: "/ " + GetText("Common_Page") }, position: ['topRight'], showSizeChanger: true, onShowSizeChange: onPaginateChange }}
            dataSource={props.members}
            rowClassName={(record: WebApiMemberDtoMemberDto, index) => record.status == 1 ? "cancelled-member" : ""}
            onRow={props.enableRowClick ? (record: WebApiMemberDtoMemberDto) => ({
                onClick: () => {
                    navigate(`/${getCurrentLanguage()}/users/${record.memberId}`);
                },
                style: { cursor: 'pointer' }
            }) : undefined}
        />
        <CustomListEditModal
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            onSuccess={() => setEditModalOpen(false)}
            customFields={modalCustomListId ? customFieldsMap[modalCustomListId] || [] : []}
            item={editItem!}
        />
        <CustomListAddModal
            open={addModalOpen}
            member={modalMember ?? undefined}
            onClose={() => setAddModalOpen(false)}
            onSuccess={() => setAddModalOpen(false)}
            customFields={modalCustomListId ? customFieldsMap[modalCustomListId] || [] : []}
            customListId={modalCustomListId || 1}
        />
    </>);
}